import { Link } from "react-router-dom";
import { ArrowRight, Tag, Sparkles, ShieldCheck, Gift, Wrench } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  DotGrid,
  FloatingShape,
  DiagonalLines,
  NumberWatermark,
  CornerBracket,
} from "@/components/Deco";
import { COMPANY } from "@/lib/site-data";

const PROMO_SECTIONS = [
  {
    n: "01",
    id: "bizalmi-ajanlat",
    icon: ShieldCheck,
    eyebrow: "Bizalmi ajánlat",
    title: "Ingyenes teherautó-ponyva állapotfelmérés.",
    text:
      "Pontosan felmérjük a ponyvát, átvizsgáljuk a tartozékokat, szakadásokat és ajánlatot küldünk.",
    badge: "INGYENES",
    cta: { label: "Ajánlatkérés", to: "/#ajanlat" },
  },
  {
    n: "02",
    id: "koltsegcsokkento",
    icon: Sparkles,
    eyebrow: "Költségcsökkentő ajánlat",
    title: "Kedvező feltételek.",
    text:
      "Beszámítjuk a régi, jó állapotú ponyváját az új termék árába.",
    badge: "BESZÁMÍTÁS",
    cta: { label: "Ajánlatkérés", to: "/#ajanlat" },
  },
  {
    n: "03",
    id: "bonusz-ajanlat",
    icon: Gift,
    eyebrow: "Bónusz ajánlat",
    title: "10+1 használt ponyva akció.",
    text:
      "10 db használt kamionponyva egyidejű vásárlása esetén egy darabot AJÁNDÉKBA adunk. Mezőgazdasági takarásra, ipari használatra ideális – több száz darabos, átvizsgált, ép állapotú készletünkből választhat.",
    badge: "10+1",
    cta: { label: "Megnézem a készletet", to: "/hasznalt-ponyvak" },
  },
  {
    n: "04",
    id: "szett-ajanlat",
    icon: Wrench,
    eyebrow: "Szettajánlat",
    title: "Medenceponyva csomag.",
    text:
      "Standard 3,6 m és 4,5 m átmérőjű medencékhez bélés- és takaróponyva-csere – csomagáron még előnyösebben, mintha külön rendelné a két elemet.",
    badge: "CSOMAG",
    cta: { label: "Medenceponyvák", to: "/medence-ponyvak" },
  },
];

export default function Akciok() {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F8FAFC] to-white" />
        <DotGrid className="absolute top-32 right-10 w-72 h-72 opacity-70" size={16} />
        <FloatingShape className="-top-32 -left-32 w-[480px] h-[480px]" color="#EA580C" />
        <NumberWatermark n="04" className="hidden md:block top-2 -right-4" />

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EA580C]/10 border border-[#EA580C]/30 text-[#EA580C] text-xs font-semibold tracking-widest uppercase">
              <Tag className="w-3.5 h-3.5" />
              Aktuális akcióink
            </div>
            <h1 className="mt-4 font-heading font-extrabold text-[#0B192C] text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.02]">
              Aktuális{" "}
              <span className="text-[#EA580C]">akcióink</span>.
            </h1>
            <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed max-w-2xl">
              Kérjen kötelezettségmentes ajánlatot telefonon vagy űrlapunkon.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Promo sections */}
      {PROMO_SECTIONS.map((p, i) => {
        const dark = i % 2 === 1;
        return (
          <section
            key={p.n}
            id={p.id}
            data-testid={`promo-${p.n}`}
            className={`relative overflow-hidden scroll-mt-24 ${
              dark ? "bg-[#0B192C] text-white" : "bg-white"
            }`}
          >
            {dark && (
              <>
                <FloatingShape className="-top-32 -right-32 w-96 h-96" color="#EA580C" />
                <DiagonalLines className="absolute inset-0 text-white" opacity={0.04} />
              </>
            )}
            {!dark && (
              <DotGrid
                className="absolute -top-10 left-1/2 -translate-x-1/2 w-[600px] h-32 opacity-50"
                color="rgba(30,62,98,0.4)"
              />
            )}

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center relative">
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7 }}
                >
                  <div
                    className={`text-xs font-heading font-bold tracking-widest uppercase ${
                      dark ? "text-[#EA580C]" : "text-[#EA580C]"
                    }`}
                  >
                    {p.n}. {p.eyebrow}
                  </div>
                  <h2
                    className={`mt-3 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight ${
                      dark ? "text-white" : "text-[#0B192C]"
                    }`}
                  >
                    {p.title}
                  </h2>
                  <p
                    className={`mt-5 text-base md:text-lg leading-relaxed max-w-xl ${
                      dark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {p.text}
                  </p>

                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <Button
                      asChild
                      className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold h-12 px-7"
                    >
                      <Link to={p.cta.to}>
                        {p.cta.label}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                    <a
                      href={`tel:${COMPANY.phoneRaw}`}
                      className={`inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md border-2 font-semibold transition-colors ${
                        dark
                          ? "border-white/30 hover:bg-white hover:text-[#0B192C] text-white"
                          : "border-[#1E3E62] hover:bg-[#1E3E62] hover:text-white text-[#1E3E62]"
                      }`}
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                </motion.div>
              </div>

              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: 0.15 }}
                  className="relative"
                >
                  {/* Big stamp/badge card */}
                  <div
                    className={`relative aspect-square max-w-md mx-auto rounded-2xl p-8 flex flex-col items-center justify-center text-center overflow-hidden ${
                      dark
                        ? "bg-gradient-to-br from-[#EA580C] to-[#C2410C] text-white"
                        : "bg-gradient-to-br from-[#1E3E62] to-[#0B192C] text-white"
                    } shadow-2xl`}
                  >
                    <CornerBracket position="tl" color="#fff" />
                    <CornerBracket position="tr" color="#fff" />
                    <CornerBracket position="bl" color="#fff" />
                    <CornerBracket position="br" color="#fff" />

                    <DiagonalLines className="absolute inset-0 text-white" opacity={0.08} />
                    <FloatingShape
                      className="-top-10 -right-10 w-40 h-40"
                      color={dark ? "#fff" : "#EA580C"}
                    />

                    <div className="relative">
                      <div className="w-20 h-20 mx-auto rounded-full bg-white/15 flex items-center justify-center backdrop-blur">
                        <p.icon className="w-10 h-10 text-white" />
                      </div>
                      <div className="mt-6 font-heading font-extrabold text-white text-4xl md:text-5xl tracking-widest">
                        {p.badge}
                      </div>
                      <div className="mt-2 text-xs uppercase tracking-[0.3em] text-white/80">
                        Akciónk · {p.n}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Final CTA */}
      <section className="bg-white py-16 md:py-20 border-t border-gray-200 relative overflow-hidden">
        <DotGrid className="absolute -top-10 right-1/4 w-72 h-32 opacity-40" />
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center relative">
          <span className="eyebrow">Kérjen ajánlatot</span>
          <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl md:text-4xl tracking-tight">
            Kérdése van bármelyik akciónkkal kapcsolatban?
          </h2>
          <p className="mt-4 text-gray-600">
            Hívjon vagy küldje el ajánlatkérését az űrlapon – szívesen segítünk.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              asChild
              className="bg-[#EA580C] hover:bg-[#C2410C] h-12 px-7 font-semibold"
            >
              <Link to="/#ajanlat">
                Ajánlatkérés <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md border-2 border-[#1E3E62] text-[#1E3E62] hover:bg-[#1E3E62] hover:text-white font-semibold transition-colors"
            >
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
